<ng-container *transloco="let t; prefix: 'workOrder'">
<div class="modal-header">
  <h5 class="modal-title mb-0">{{ t('modals.eventSchedule.title') }}</h5>

  <div class="d-flex align-items-center gap-4">
    @if (!isEditingDetails) {
      <button
        type="button"
        class="btn-close"
        [attr.aria-label]="t('modals.eventSchedule.toggleEdit')"
        (click)="toggleEditDetails()">
        <i class="ph ph-pencil-simple"></i>
      </button>
    }

    <button
      type="button"
      class="btn-close"
      [attr.aria-label]="'common.close' | transloco"
      (click)="closeModal()">
      <i class="ph ph-x"></i>
    </button>
  </div>
</div>

<div class="modal-body">
  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <!-- Asset Name (always readonly) -->
    <div class="form-group">
      <label>{{ t('modals.eventSchedule.assetName') }}</label>
      <input
        class="form-control readonly"
        type="text"
        [value]="assetLabel"
        readonly />
    </div>

    <!-- Make + Model -->
    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('modals.eventSchedule.make') }}</label>
        <input
          class="form-control readonly"
          type="text"
          formControlName="make"
          readonly />
      </div>

      <div class="form-group half-width">
        <label>{{ t('modals.eventSchedule.model') }}</label>
        <input
          class="form-control readonly"
          type="text"
          formControlName="model"
          readonly />
      </div>
    </div>

    <!-- Odometer Reading + Maintenance Type -->
    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('modals.eventSchedule.odometerReading') }}</label>
        <input
          class="form-control readonly"
          type="text"
          formControlName="odometer"
          readonly />
      </div>

      <div class="form-group half-width">
        <label>{{ t('modals.eventSchedule.maintenanceType') }}</label>

        @if (isEditingDetails) {
          <div ngbDropdown class="custom-dropdown" [autoClose]="true">
            <button
              type="button"
              class="btn form-control dropdown-toggle"
              ngbDropdownToggle>
              <div
                class="d-flex flex-row justify-content-between dropdown-inner">
                <span class="align-middle">{{ maintenanceTypeLabel }}</span>
                <i class="ph ph-caret-down lh-base"></i>
              </div>
            </button>

            <div ngbDropdownMenu>
              <div class="d-flex flex-column">
                @for (t of maintenanceTypes; track t.value) {
                  <button
                    type="button"
                    class="btn btn-outline"
                    ngbDropdownItem
                    (click)="onMaintenanceTypeSelect(t, $event)">
                    {{ t.label | transloco }}
                  </button>
                }
              </div>
            </div>
          </div>
        } @else {
          <input
            class="form-control readonly"
            type="text"
            [value]="maintenanceTypeLabel"
            readonly />
        }
      </div>
    </div>

    <!-- Service Centre + Scheduled Date -->
    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('modals.eventSchedule.serviceCentre') }}</label>

        @if (isEditingDetails) {
          <div ngbDropdown class="custom-dropdown" [autoClose]="true">
            <button
              type="button"
              class="btn form-control dropdown-toggle"
              ngbDropdownToggle>
              <div
                class="d-flex flex-row justify-content-between dropdown-inner">
                <span class="align-middle">{{ serviceCentreLabel }}</span>
                <i class="ph ph-caret-down lh-base"></i>
              </div>
            </button>

            <div ngbDropdownMenu class="service-centre-menu">
              <div class="d-flex flex-column service-centre-scroll">
                @for (w of workshops; track w.id) {
                  <button
                    type="button"
                    class="btn btn-outline"
                    ngbDropdownItem
                    (click)="onServiceCentreSelect(w, $event)">
                    {{ w.name }}
                  </button>
                }
              </div>
            </div>
          </div>
        } @else {
          <input
            class="form-control readonly"
            type="text"
            [value]="serviceCentreLabel"
            readonly />
        }
      </div>

      <div class="form-group half-width">
        @if (isEditingDetails) {
          <app-flatpickr-date-picker
            [label]="t('modals.eventSchedule.scheduledDate')"
            [value]="scheduledDateValue"
            (valueChange)="onScheduledDateChange($event)"
            [minDate]="'today'"
            [dateFormat]="'Y/m/d H:i'"
            [altFormat]="'Y/m/d H:i'"
            [enableTime]="true"
            [time24hr]="true"
            [yearsAhead]="5"
            [yearsBack]="0"
            [required]="true"
            [placeholder]="t('modals.shared.dateTimePlaceholder')">
          </app-flatpickr-date-picker>
        } @else {
          <label>{{ t('modals.eventSchedule.scheduledDate') }}</label>
          <input
            class="form-control readonly"
            type="text"
            [value]="scheduledDateValue"
            readonly />
        }

        @if (
          form.get('scheduledDate')?.invalid &&
          form.get('scheduledDate')?.touched
        ) {
          <div class="error-message">{{ t('modals.shared.scheduledDateRequired') }}</div>
        }
      </div>
    </div>

    <!-- Service Items -->
    <div class="form-group parts-section" (click)="$event.stopPropagation()">
      <label>{{ t('modals.eventSchedule.serviceItems') }}</label>

      <!-- Selected parts -->
      @if (selectedItems.length === 0) {
        <span class="parts-placeholder">
          {{
            isServiceItemsLoading
              ? t('modals.eventSchedule.loadingServiceItems')
              : t('modals.eventSchedule.noServiceItems')
          }}
        </span>
      } @else {
        <div class="pill-scroller" [class.is-expanded]="showAllParts">
          <div class="pill-scroller__viewport">
            <div
              class="pill-scroller__track"
              #pillScroller
              (scroll)="onPillScroll()">
              @for (
                part of selectedItems;
                track part.partId 
              ) {
                <span class="part-pill" [title]="partPillTitle(part)">
                  <span class="part-pill__badge">{{ part.quantity }}</span>

                  <span class="part-pill__info">
                    <span class="part-pill__title">
                      <span class="part-pill__name">{{ part.partName }}</span>
                      @if (part.partNumber) {
                        <span class="part-pill__number">{{
                          part.partNumber
                        }}</span>
                      }
                    </span>

                    @if (partMetaLabel(part); as meta) {
                      <span class="part-pill__number">{{ meta }}</span>
                    }
                  </span>
                </span>
              }
            </div>

            @if (showPillLeftArrow) {
              <button
                type="button"
                class="pill-scroller__arrow pill-scroller__arrow--left"
                [attr.aria-label]="t('modals.eventSchedule.scrollLeft')"
                (click)="scrollPills(-1)">
                <i class="ph ph-caret-left"></i>
              </button>
            }

            @if (showPillRightArrow) {
              <button
                type="button"
                class="pill-scroller__arrow pill-scroller__arrow--right"
                [attr.aria-label]="t('modals.eventSchedule.scrollRight')"
                (click)="scrollPills(1)">
                <i class="ph ph-caret-right"></i>
              </button>
            }
          </div>

          @if (showAllParts || showPillRightArrow || showPillLeftArrow) {
            <button
              type="button"
              class="see-all-link"
              (click)="toggleShowAllParts()">
              {{ showAllParts ? t('modals.eventSchedule.showLess') : ('common.seeAll' | transloco) }}
            </button>
          }
        </div>
      }
    </div>

    <!-- Footer -->
    <div class="modal-footer">
      @if (isEditingDetails) {
        <button
          type="button"
          class="cancel-btn"
          (click)="onCancel()"
          [disabled]="isLoading">
          {{ 'common.cancel' | transloco }}
        </button>
        <button type="submit" class="add-btn" [disabled]="isLoading">
          @if (isLoading) {
            <span class="spinner-border"></span>
          }
          @if (!isLoading) {
            <span>{{ t('modals.shared.saveChanges') }}</span>
          }
        </button>
      }
    </div>
  </form>
</div>
</ng-container>
